'use client';
import { getAnonUser } from './utils';
import { getOptimizelyAttributes } from './attributes';
import { pushToDataLayer } from 'services/Gtm/functions';
import Cookies from 'js-cookie';
import { EventEmitter } from 'events';
import { useEffect, useRef, useState, useCallback } from 'react';
import optimizelySdk, { type OptimizelyUserContext } from '@optimizely/optimizely-sdk';
import type CustomWindow from 'interfaces/Utils';
import type { PageAdConfig } from '@/app_lib/types/Ad';
import { useReportWebVitals } from 'next/web-vitals';
import { OptimizelyContext } from '@/main/lib/hooks/useOptimizely';

declare let window: CustomWindow;

const optimizely = optimizelySdk.createInstance({
  sdkKey: process.env.NEXT_PUBLIC_OPTIMIZELY_KEY,
});

export const userFlagsEmitter = new EventEmitter();
const primaryTrackingMetrics = [
  'leaderboard_ad',
  'scroll_depth',
  'time_on_page',
  'saw_first_ad',
  'saw_second_ad',
  'saw_third_ad',
  'saw_fourth_ad',
  'saw_fifth_ad',
  'finished_article',
  'saw_outbrain',
  'saw_stn_video',
  'ad_impressions',
  'return_visits_via_utm',
];
let _cachedUser: OptimizelyUserContext | null = null;

export const getUserContext = async (pageAdConfig?: PageAdConfig) => {
  if (_cachedUser) return _cachedUser;
  const attributes = await getOptimizelyAttributes(pageAdConfig);
  const userId = Cookies.get('pianoUid') || getAnonUser();
  const user = optimizely?.createUserContext(userId, attributes) ?? null;
  if (!user) return null;

  // initialize timestamps used to compute active engagement time (milliseconds)
  try {
    const now = Date.now();
    const visible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
    // @ts-ignore - attach internal fields to user object
    (user as any).__lastTimeOnPageSentAt = now;
    (user as any).__lastActiveAt = visible ? now : 0;

    const visibilityHandler = () => {
      try {
        // if it becomes visible, mark last active
        if (document.visibilityState === 'visible') {
          (user as any).__lastActiveAt = Date.now();
          return;
        }

        // when it becomes hidden (or not visible), mark inactive
        (user as any).__lastActiveAt = 0;

        // send a single summary beacon the first time page becomes hidden
        if (!(user as any).__visibilityBeaconSent) {
          (user as any).__visibilityBeaconSent = true;
          // Build test/variant summary using current tests available on page
          const metrics: Record<string, number> = {};
          let hasMetrics = false;
          for (const metric of primaryTrackingMetrics) {
            try {
              const metricValue = sessionStorage.getItem(metric);
              if (metricValue) {
                metrics[`opt_${metric}`] = Number(metricValue);
                hasMetrics = true;
                sessionStorage.removeItem(metric);
              }
            } catch {}
          }

          const payload = {
            path: typeof location !== 'undefined' ? location.pathname : '',
            event: 'optimizely_tracking_event',
            opt_test_info: '',
            ...metrics,
          };

          try {
            let testInfo = '{';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const allTests: any[] = (globalThis as any).__optimizely_current_tests || [];
            // if not populated, fallback to currentTests import by requiring module-level var (no-op on client)
            if (Array.isArray(allTests) && allTests.length > 0) {
              for (const t of allTests) {
                try {
                  // attempt to decide on each known test using the user object
                  if (t?.testName && (user as any).decide) {
                    const decision = (user as any).decide(t.testName);
                    if (decision?.enabled) {
                      testInfo += ` ${t.testName} : ${decision.variationKey}, `;
                    }
                  }
                } catch {}
              }
            }
            testInfo += 'opt: true}';
            payload.opt_test_info = testInfo;
            if (hasMetrics) pushToDataLayer(payload);
          } catch {}
        }
      } catch {}
    };

    // avoid duplicate handlers on same user object
    if (
      typeof document !== 'undefined' &&
      typeof document.addEventListener === 'function' &&
      !(user as any).__visibilityHandler
    ) {
      document.addEventListener('visibilitychange', visibilityHandler);
      // @ts-ignore
      (user as any).__visibilityHandler = visibilityHandler;
    }

    if ((user as any).trackEvent) {
      const originalTrack = (user as any).trackEvent.bind(user);
      // @ts-ignore
      (user as any).trackEvent = async (eventKey: string, eventTags?: Record<string, any>) => {
        try {
          const nowEvent = Date.now();
          const lastSent = (user as any).__lastTimeOnPageSentAt || nowEvent;
          const wasActive = (user as any).__lastActiveAt && (user as any).__lastActiveAt > 0;
          const activeDuration = wasActive && document.visibilityState === 'visible' ? nowEvent - lastSent : 0;

          // @ts-ignore
          await originalTrack(eventKey, eventTags);
          if (activeDuration > 0) {
            try {
              // @ts-ignore
              await originalTrack('time_on_page', {
                value: activeDuration / 1000, // convert ms to seconds
                $opt_event_properties: { from_event: eventKey },
              });
              // add time_on_page to session storage
              const currentTimeOnPage = sessionStorage.getItem('time_on_page');
              const newTimeOnPage = currentTimeOnPage
                ? Number(currentTimeOnPage) + Math.round(activeDuration / 1000)
                : Math.round(activeDuration / 1000);
              sessionStorage.setItem('time_on_page', newTimeOnPage.toString());
            } catch {
              // ignore errors sending time_on_page
            }
          }

          // update the lastTimeOnPageSentAt timestamp regardless so interval won't duplicate
          (user as any).__lastTimeOnPageSentAt = nowEvent;
          if (document.visibilityState === 'visible') (user as any).__lastActiveAt = nowEvent;
        } catch {}
      };
    }
  } catch {
    // ignore any errors on attaching handlers
  }

  _cachedUser = user;
  return _cachedUser;
};

export interface VariantProps {
  variant?: string | null;
  enabled?: boolean;
  variables?: any;
}

const OPTIMIZELY_FLAGS = [
  'readingProgressBar',
  'hideHamburgerMenu_desktop',
  'readTime',
  'hideTopicMenu_mobile',
  'desktop_right_rail',
  'add_us_on_google_banner',
  'recurlyEngageModalTrigger',
  'bodyUnderlineLinks',
] as const;
export type OptimizelyFlags = (typeof OPTIMIZELY_FLAGS)[number];

export interface OptimizelyUserInfo {
  user?: OptimizelyUserContext;
  // TODO: remove string and update the OptimizelyFlags
  [key: string]: VariantProps | OptimizelyUserContext | undefined;
}

const currentTests = [
  {
    location: ['2025', '2024', 'article'],
    tags: [],
    excludedTags: [],
    section: [],
    excludedSections: [],
    testName: 'socialShareV2',
  },
  { location: ['section'], testName: 'section' },
  { location: ['checkout'], testName: 'helio-setup' },
  { location: ['education'], testName: 'education' },
  { location: ['article'], testName: 'dateless-article' },
  { location: ['*'], testName: 'crownInitalHeight' },
  { location: ['2025', '2024', 'article'], testName: 'articleStickyFooter' },
  {
    location: ['reviews-1', 'reviews-2', 'reviews-1/top-5-companies/', 'reviews-2/top-5-companies/'],
    testName: 'ndrTracking',
  },
  { location: ['people'], testName: 'peoplePage' },
  { location: ['2025', '2024', '2022', 'article'], testName: 'shrink_spacing_articles' },
  { location: ['2026', '2025', '2024', 'article'], testName: 'desktop_right_rail' },
  {
    location: ['article', '2024', '2025'],
    primarySection: [
      'leadership',
      'future-of-work',
      'workplace-culture',
      'c-suite',
      'success',
      'politics',
      'innovation',
    ],
    excludedTags: ['always free', 'health evergreen', 'personal finance evergreen'],
    tags: ['ceo daily', 'chief executive office (ceo)', 'executives', 'federal reserve', 'tariffs and trade'],
    testName: 'pianoTemplateLocation',
    excludedPremiumCategory: 'f-partnership',
    evaluateTagsAndSectionsSeparately: true,
  },
  { location: ['*'], testName: 'hideHamburgerMenu_desktop' },
  { location: ['*'], testName: 'hideTopicMenu_mobile' },
  { location: ['section'], testName: 'replatform-section-redirect' },
  { location: ['tag'], testName: 'replatform-tag-redirect' },
  { location: ['2025', '2024', 'article', 'next'], testName: 'readTime' },
  { location: ['2025', '2024', 'article'], testName: 'readingProgressBar' },
  { location: ['2025', '2024', 'article'], testName: 'nativeshare' },
  { location: ['2025', '2024', 'article', 'education'], testName: 'add_us_on_google_banner' },
  // TODO: Because of client side navigation stopping the Optimizely Provider from re-running, we will let any account page run the test
  // TODO: This ensures that once in the /account eco-system, the test will have run, so if they navigate internally or go directly to it, the test will have run.
  // TODO: Long term, we should update the Optimizely Provider to re-run when pathname changes, but for this test this works
  { location: ['account/'], testName: 'recurlyEngageModalTrigger' },
  { location: ['2025', '2024', 'article'], testName: 'bodyUnderlineLinks' },
];

interface TestConfig {
  location: string[];
  primarySection?: string[];
  tags?: string[];
  excludedTags?: string[];
  excludedSections?: string[];
  section?: string[];
  excludedPremiumCategory?: string;
  evaluateTagsAndSectionsSeparately?: boolean;
  [key: string]: any;
}

const canRunTest = (test: TestConfig, pageAdConfig?: PageAdConfig) => {
  try {
    // Test rules
    const {
      location,
      primarySection,
      tags,
      excludedTags,
      excludedSections,
      excludedPremiumCategory,
      section,
      evaluateTagsAndSectionsSeparately,
    } = test;

    // Page ad config values & location to compare against
    // Primary Section or Page Channel
    const ch = pageAdConfig?.ch?.toLowerCase?.();
    const pageAdConfigTags = (pageAdConfig?.tags || []).map((tag: string) => tag.toLowerCase());
    const pageAdConfigPremiumCategory = pageAdConfig?.premiumCategory?.toLowerCase() || null;
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const locationInPath = location.some((loc) => path.includes(loc)) || location[0] === '*';

    // Default if no sections are given as primary or general sections
    let sectionMatches = true;
    if (section && section.length > 0) {
      sectionMatches = section.some((sec) => ch === sec);
    } else if (primarySection && primarySection.length > 0) {
      sectionMatches = primarySection.some((sec) => ch === sec) || primarySection[0] === '*';
    }

    // Check tags from test rule against pageAdConfig tags
    const tagsInPageAdConfig = tags ? tags.some((tag) => pageAdConfigTags.includes(tag)) || tags[0] === '*' : true;

    // Check exclusions for sections, tags, and premium category
    const excludedSectionsNotInPageAdConfig = excludedSections ? !excludedSections.some((sec) => ch === sec) : true;
    const excludedTagsNotInPageAdConfig = excludedTags
      ? !excludedTags.some((tag) => pageAdConfigTags.includes(tag))
      : true;
    const excludedPremiumCategoryNotInPageAdConfig = excludedPremiumCategory
      ? excludedPremiumCategory !== pageAdConfigPremiumCategory
      : true;

    const validTagsAndSections = evaluateTagsAndSectionsSeparately
      ? sectionMatches || tagsInPageAdConfig
      : sectionMatches && tagsInPageAdConfig;

    const hasNoExclusions =
      excludedTagsNotInPageAdConfig && excludedSectionsNotInPageAdConfig && excludedPremiumCategoryNotInPageAdConfig;

    return locationInPath && validTagsAndSections && hasNoExclusions;
  } catch {
    return false;
  }
};

export type OptimizelySdk = typeof optimizelySdk;

export const OptimizelyProvider = ({
  children,
  pageAdConfig,
}: {
  children: React.ReactNode;
  pageAdConfig?: PageAdConfig;
}) => {
  const [userFlags, setUserFlags] = useState<OptimizelyUserInfo>({});

  // persist scroll depth between renders
  const scrollDepthRef = useRef<number>(0);

  const reportWebVitalsCallback = useCallback(
    async (metric: any) => {
      try {
        const user = await getUserContext(pageAdConfig);
        if (user) {
          const properties = {
            metric_name: metric.name, // e.g. FCP, LCP, CLS, TTFB , FID, INP.
            metric_id: metric.id,
          };
          const tags = {
            value: metric.value,
            $opt_event_properties: properties,
          };
          // @ts-ignore
          user.trackEvent(metric.name, tags);
        }
      } catch {}
    },
    [pageAdConfig],
  );

  useReportWebVitals(reportWebVitalsCallback);

  // stable scroll listener
  const scrollListener = useCallback(async () => {
    try {
      const user = await getUserContext(pageAdConfig);
      const scrolled = window.scrollY + window.innerHeight;
      const totalHeight = document.documentElement.scrollHeight || 1;
      const depth = Math.round((scrolled / totalHeight) * 100);
      if (depth > scrollDepthRef.current + 5) {
        const depthToAdd = depth - scrollDepthRef.current;
        scrollDepthRef.current = depth;
        if (user) {
          // @ts-ignore
          user.trackEvent('scroll_depth', { value: depthToAdd });
          // add scroll depth to session storage
          try {
            const currentScrollDepth = sessionStorage.getItem('scroll_depth');
            const newScrollDepth = currentScrollDepth ? Number(currentScrollDepth) + depthToAdd : depthToAdd;
            sessionStorage.setItem('scroll_depth', newScrollDepth.toString());
          } catch {}
        }
      }
    } catch {}
  }, [pageAdConfig]);

  useEffect(() => {
    // Invalidate module-level cache when pageAdConfig changes to ensure attributes/user are correct
    _cachedUser = null;

    // expose currentTests for visibility handler fallback
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (globalThis as any).__optimizely_current_tests = currentTests;

    // remove any old scroll listener and add a stable one
    try {
      window.removeEventListener('scroll', scrollListener);
    } catch {}

    const beforeUnloadHandler = async () => {
      try {
        const user = await getUserContext(pageAdConfig);
        if (user) {
          user.trackEvent('final_timer', { value: 1 });
        }
      } catch {}
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);

    const calculateScrollDepth = () => {
      window.addEventListener('scroll', scrollListener, { passive: true });
    };

    void optimizely?.onReady().then(async ({ success }) => {
      if (!success || !optimizely) {
        return;
      }

      // ensure module-level cache is primed
      const user = await getUserContext(pageAdConfig);

      if (!user) return;

      const newUserFlags: OptimizelyUserInfo = { user };
      const urlParams = new URLSearchParams(window.location.search);

      for (let i = 0; i !== currentTests.length; i += 1) {
        const { testName } = currentTests[i];
        const testOverride = urlParams.get(testName);
        const hasUrlOverride = testOverride !== null;

        const canRun = hasUrlOverride || canRunTest(currentTests[i], pageAdConfig);

        if (!canRun) {
          newUserFlags[testName] = {
            enabled: false,
            variables: {},
            variant: null,
          };
          continue;
        }

        try {
          const decision = user.decide(testName);
          const testOverrideBoolean = testOverride ? testOverride.toLowerCase() !== 'off' : decision.enabled;
          const testOverrideVariant = testOverride ? testOverride.toLowerCase() : decision.variationKey;

          newUserFlags[testName] = {
            enabled: testOverrideBoolean,
            variables: decision.variables,
            variant: testOverrideVariant,
          };
        } catch {
          newUserFlags[testName] = {
            enabled: false,
            variables: {},
            variant: null,
          };
        }
      }

      setUserFlags(newUserFlags);
      user.trackEvent('pages_visited', { value: 1 });

      // Track return visits via UTM native share
      const utmSource = urlParams.get('utm_source');
      if (utmSource === 'native_share') {
        user.trackEvent('return_visits_via_utm', { value: 1 });
      }

      userFlagsEmitter.emit('userFlagsUpdated', newUserFlags);
    });

    calculateScrollDepth();

    return () => {
      try {
        window.removeEventListener('scroll', scrollListener);
      } catch {}
      try {
        window.removeEventListener('beforeunload', beforeUnloadHandler);
      } catch {}
    };
  }, [pageAdConfig, scrollListener]);
  // @ts-ignore
  return <OptimizelyContext.Provider value={{ userFlags, optimizely }}>{children}</OptimizelyContext.Provider>;
};

export const trackOptimizelyEvent = async (event: string, properties: Record<string, string | number>) => {
  try {
    const user = await getUserContext(window?.arcSpanPageConfig);
    if (!user) return;

    const { value, ...rest } = properties ?? {};
    const tags = {
      value: value || 0,
      $opt_event_properties: rest || {},
    };
    // @ts-ignore

    user.trackEvent(event, tags);
    if (event.includes('ad') || event.includes('signed_up')) {
      try {
        const currentAdImpressions = sessionStorage.getItem('ad_impressions');
        const newAdImpressions = currentAdImpressions ? Number(currentAdImpressions) + 1 : 1;
        sessionStorage.setItem('ad_impressions', newAdImpressions.toString());
      } catch {}
    }
    sessionStorage.setItem(event, '1');
  } catch {}
};

export const useIntersectionTracking = (elementId: string, properties: Record<string, any>, event: string) => {
  const firedKey = `${elementId}::${event}`;
  // initialize global fired set
  // @ts-ignore
  if (!(globalThis as any).__optimizely_fired_tracking_set) {
    // @ts-ignore
    (globalThis as any).__optimizely_fired_tracking_set = new Set<string>();
  }
  // @ts-ignore
  const firedSet: Set<string> = (globalThis as any).__optimizely_fired_tracking_set;

  useEffect(() => {
    if (firedSet.has(firedKey)) return;

    let observer: IntersectionObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const tryAttach = () => {
      const target = document.getElementById(elementId);
      if (!target || firedSet.has(firedKey)) return;

      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              firedSet.add(firedKey);
              if (observer) observer.disconnect();
              (async () => {
                try {
                  const user = await getUserContext(window?.arcSpanPageConfig);
                  if (user) {
                    if (event.includes('ad') || event.includes('stn') || event.includes('outbrain')) {
                      // @ts-ignore
                      user.trackEvent('ad_impressions', { value: properties?.value || 0 });
                      // increase the add impressions in session storage
                      try {
                        const currentAdImpressions = sessionStorage.getItem('ad_impressions');
                        const newAdImpressions = currentAdImpressions ? Number(currentAdImpressions) + 1 : 1;
                        sessionStorage.setItem('ad_impressions', newAdImpressions.toString());
                      } catch {}
                    }
                    const tags = { value: properties?.value || 0, $opt_event_properties: properties || {} };
                    sessionStorage.setItem(event, '1');
                    // @ts-ignore
                    user.trackEvent(event, tags);
                  }
                } catch {}
              })();
            }
          }
        },
        { threshold: 0.5 },
      );

      observer.observe(target);
    };
    setTimeout(() => {
      if (!document.getElementById(elementId)) {
        mutationObserver = new MutationObserver(() => {
          if (document.getElementById(elementId)) {
            tryAttach();
            if (mutationObserver) mutationObserver.disconnect();
          }
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });
      } else {
        tryAttach();
      }
    }, 200);

    return () => {
      if (observer) observer.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
    };
    // stringify properties to avoid unnecessary re-runs
  }, [elementId, event, JSON.stringify(properties)]);
};

export const OptimizelyScript: React.FC = () => null;
